import axios, { AxiosResponse } from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const EVENTS_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const eventsClient = axios.create({
  baseURL: EVENTS_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

eventsClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

eventsClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

// --- Status Types ---
export type EventSpaceStatus = 'available' | 'maintenance' | 'reserved' | 'unavailable';
export type EventBookingStatus =
  | 'inquiry'
  | 'quoted'
  | 'confirmed'
  | 'pending_payment'
  | 'fully_paid'
  | 'setup'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show';
export type EventType =
  | 'wedding'
  | 'conference'
  | 'corporate'
  | 'birthday'
  | 'anniversary'
  | 'gala'
  | 'workshop'
  | 'concert'
  | 'exhibition'
  | 'private_dining'
  | 'other';
export type EventPaymentStatus =
  | 'pending'
  | 'deposit_paid'
  | 'partially_paid'
  | 'fully_paid'
  | 'refunded'
  | 'failed';
export type AttendeeType =
  | 'vip'
  | 'speaker'
  | 'organizer'
  | 'general'
  | 'staff'
  | 'media'
  | 'vendor';

// --- EventSpace ---
export interface EventSpace {
  id: string;
  name: string;
  description?: string;
  code: string;
  space_type: string;
  capacity_min: number;
  capacity_max: number;
  area_sqm?: number;
  hourly_rate?: number;
  half_day_rate?: number;
  full_day_rate?: number;
  status: EventSpaceStatus;
  floor_level?: number;
  location?: string;
  amenities?: string[];
  images?: string[];
  is_active: boolean;
  requires_deposit: boolean;
  deposit_amount?: number;
  cancellation_policy?: string;
  setup_time_minutes: number;
  teardown_time_minutes: number;
  max_booking_days: number;
  created_at: string;
  updated_at: string;
}

// --- EventPackage ---
export interface EventPackage {
  id: string;
  name: string;
  description?: string;
  code: string;
  package_type: string;
  base_price: number;
  price_per_person?: number;
  min_attendees?: number;
  max_attendees?: number;
  includes_equipment?: string[];
  includes_services?: string[];
  includes_items?: string[];
  menu_options?: string[];
  customizations?: string[];
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// --- EventBooking ---
export interface EventBookingPackage {
  id: string;
  event_booking_id: string;
  package_id: string;
  package?: EventPackage;
  quantity: number;
  unit_price: number;
  total_price: number;
  created_at: string;
}

export interface EventBooking {
  id: string;
  booking_reference: string;
  event_space_id: string;
  event_space?: EventSpace;
  event_type: EventType;
  event_name: string;
  event_description?: string;
  host_name: string;
  host_email: string;
  host_phone: string;
  host_organization?: string;
  guest_id?: string;
  booking_id?: string;
  source: string;
  status: EventBookingStatus;
  event_date: string;
  start_time: string;
  end_time: string;
  setup_start_time?: string;
  teardown_end_time?: string;
  expected_attendees: number;
  actual_attendees?: number;
  confirmed_attendees?: number;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  service_charge: number;
  deposit_amount: number;
  total_amount: number;
  paid_amount: number;
  payment_status: EventPaymentStatus;
  currency: string;
  special_requirements?: string;
  dietary_requirements?: string;
  theme_preferences?: string;
  decoration_notes?: string;
  audio_visual_requirements?: string;
  parking_requirements?: number;
  security_required: boolean;
  catering_required: boolean;
  coordinator_id?: string;
  coordinator_notes?: string;
  contract_sent_at?: string;
  contract_signed_at?: string;
  deposit_paid_at?: string;
  final_payment_due_date?: string;
  final_payment_paid_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  cancellation_fee?: number;
  no_show_at?: string;
  metadata?: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
  packages?: EventBookingPackage[];
  attendees?: EventAttendee[];
  equipment?: EventBookingEquipment[];
}

// --- EventAttendee ---
export interface EventAttendee {
  id: string;
  event_booking_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  attendee_type: AttendeeType;
  organization?: string;
  designation?: string;
  dietary_restrictions?: string;
  accessibility_needs?: string;
  meal_preference?: string;
  table_number?: string;
  seat_number?: string;
  check_in_status: string;
  checked_in_at?: string;
  special_notes?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

// --- EventEquipment ---
export interface EventEquipment {
  id: string;
  name: string;
  code: string;
  description?: string;
  category?: string;
  quantity_available: number;
  quantity_total: number;
  unit_cost?: number;
  rental_price?: number;
  is_rented_out: boolean;
  requires_setup: boolean;
  setup_time_minutes?: number;
  is_active: boolean;
  maintenance_due?: string;
  last_maintenance?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// --- EventBookingEquipment ---
export interface EventBookingEquipment {
  id: string;
  event_booking_id: string;
  equipment_id: string;
  equipment?: EventEquipment;
  quantity_requested: number;
  quantity_confirmed: number;
  unit_price: number;
  total_price: number;
  setup_required: boolean;
  setup_start_time?: string;
  notes?: string;
  status: string;
  returned_at?: string;
  condition_on_return?: string;
  damage_notes?: string;
  created_at: string;
  updated_at: string;
}

// --- Paginated Responses ---
export interface PaginatedSpaces {
  spaces: EventSpace[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedPackages {
  packages: EventPackage[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedEventBookings {
  bookings: EventBooking[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedEquipment {
  equipment: EventEquipment[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// --- Space Service ---
export interface SpaceListParams {
  page?: number;
  page_size?: number;
  status?: EventSpaceStatus;
  space_type?: string;
}

export interface SpaceAvailableParams {
  min_capacity?: number;
  date?: string;
}

export const spaceService = {
  list: async (params?: SpaceListParams): Promise<PaginatedSpaces> => {
    const res = await eventsClient.get<PaginatedSpaces>('/api/v1/event-spaces', { params });
    return res.data;
  },
  getAll: async (): Promise<EventSpace[]> => {
    const res = await eventsClient.get<EventSpace[]>('/api/v1/event-spaces/all');
    return res.data;
  },
  getAvailable: async (params?: SpaceAvailableParams): Promise<EventSpace[]> => {
    const res = await eventsClient.get<EventSpace[]>('/api/v1/event-spaces/available', { params });
    return res.data;
  },
  get: (id: string) => eventsClient.get<EventSpace>(`/api/v1/event-spaces/${id}`),
  create: (data: Partial<EventSpace>) =>
    eventsClient.post<EventSpace>('/api/v1/event-spaces', data),
  update: (id: string, data: Partial<EventSpace>) =>
    eventsClient.put<EventSpace>(`/api/v1/event-spaces/${id}`, data),
  delete: (id: string) => eventsClient.delete(`/api/v1/event-spaces/${id}`),
  updateStatus: (id: string, status: EventSpaceStatus) =>
    eventsClient.put<EventSpace>(`/api/v1/event-spaces/${id}/status`, { status }),
};

// --- Package Service ---
export const packageService = {
  list: async (params?: { page?: number; page_size?: number }): Promise<PaginatedPackages> => {
    const res = await eventsClient.get<PaginatedPackages>('/api/v1/event-packages', { params });
    return res.data;
  },
  getAll: async (): Promise<EventPackage[]> => {
    const res = await eventsClient.get<EventPackage[]>('/api/v1/event-packages/all');
    return res.data;
  },
  getFeatured: async (): Promise<EventPackage[]> => {
    const res = await eventsClient.get<EventPackage[]>('/api/v1/event-packages/featured');
    return res.data;
  },
  getByType: async (type: string): Promise<EventPackage[]> => {
    const res = await eventsClient.get<EventPackage[]>(`/api/v1/event-packages/type/${type}`);
    return res.data;
  },
  get: (id: string) => eventsClient.get<EventPackage>(`/api/v1/event-packages/${id}`),
  create: (data: Partial<EventPackage>) =>
    eventsClient.post<EventPackage>('/api/v1/event-packages', data),
  update: (id: string, data: Partial<EventPackage>) =>
    eventsClient.put<EventPackage>(`/api/v1/event-packages/${id}`, data),
  delete: (id: string) => eventsClient.delete(`/api/v1/event-packages/${id}`),
};

// --- Booking Service ---
export interface EventBookingListParams {
  page?: number;
  page_size?: number;
  status?: EventBookingStatus;
  event_type?: EventType;
  start_date?: string;
  end_date?: string;
}

export interface CreateEventBookingInput {
  event_space_id: string;
  event_type: EventType;
  event_name: string;
  event_description?: string;
  host_name: string;
  host_email: string;
  host_phone: string;
  host_organization?: string;
  event_date: string;
  start_time: string;
  end_time: string;
  setup_start_time?: string;
  teardown_end_time?: string;
  expected_attendees: number;
  subtotal?: number;
  tax_amount?: number;
  discount_amount?: number;
  service_charge?: number;
  deposit_amount?: number;
  total_amount?: number;
  special_requirements?: string;
  dietary_requirements?: string;
  theme_preferences?: string;
  decoration_notes?: string;
  audio_visual_requirements?: string;
  parking_requirements?: number;
  security_required?: boolean;
  catering_required?: boolean;
  coordinator_id?: string;
  coordinator_notes?: string;
  package_ids?: string[];
  metadata?: Record<string, unknown>;
}

const bookingService = {
  list: async (params?: EventBookingListParams): Promise<PaginatedEventBookings> => {
    const res = await eventsClient.get<PaginatedEventBookings>('/api/v1/event-bookings', {
      params,
    });
    return res.data;
  },
  getByReference: (ref: string) =>
    eventsClient.get<EventBooking>(`/api/v1/event-bookings/reference/${ref}`),
  getByDate: async (date: string): Promise<EventBooking[]> => {
    const res = await eventsClient.get<EventBooking[]>(`/api/v1/event-bookings/date/${date}`);
    return res.data;
  },
  get: (id: string) => eventsClient.get<EventBooking>(`/api/v1/event-bookings/${id}`),
  create: (data: CreateEventBookingInput) =>
    eventsClient.post<EventBooking>('/api/v1/event-bookings', data),
  update: (id: string, data: Partial<EventBooking>) =>
    eventsClient.put<EventBooking>(`/api/v1/event-bookings/${id}`, data),
  delete: (id: string) => eventsClient.delete(`/api/v1/event-bookings/${id}`),
  confirm: (id: string) => eventsClient.post<EventBooking>(`/api/v1/event-bookings/${id}/confirm`),
  cancel: (id: string, reason?: string) =>
    eventsClient.post<EventBooking>(`/api/v1/event-bookings/${id}/cancel`, { reason }),
  markNoShow: (id: string) =>
    eventsClient.post<EventBooking>(`/api/v1/event-bookings/${id}/no-show`),
  checkIn: (id: string) => eventsClient.post<EventBooking>(`/api/v1/event-bookings/${id}/check-in`),
  checkOut: (id: string) =>
    eventsClient.post<EventBooking>(`/api/v1/event-bookings/${id}/check-out`),
  processPayment: (id: string, phone: string) =>
    eventsClient.post<EventBooking>(`/api/v1/event-bookings/${id}/pay`, { phone }),
};

export const eventBookingService = bookingService;

// --- Attendee Service ---
export interface CreateAttendeeInput {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  attendee_type: AttendeeType;
  organization?: string;
  designation?: string;
  dietary_restrictions?: string;
  accessibility_needs?: string;
  meal_preference?: string;
  table_number?: string;
  seat_number?: string;
  special_notes?: string;
  tags?: string[];
}

export const attendeeService = {
  listByBooking: async (bookingId: string): Promise<EventAttendee[]> => {
    const res = await eventsClient.get<EventAttendee[]>(
      `/api/v1/event-bookings/${bookingId}/attendees`,
    );
    return res.data;
  },
  create: (bookingId: string, data: CreateAttendeeInput) =>
    eventsClient.post<EventAttendee>(`/api/v1/event-bookings/${bookingId}/attendees`, data),
  createBatch: (bookingId: string, data: CreateAttendeeInput[]) =>
    eventsClient.post<EventAttendee[]>(`/api/v1/event-bookings/${bookingId}/attendees/batch`, data),
  get: (id: string) => eventsClient.get<EventAttendee>(`/api/v1/attendees/${id}`),
  update: (id: string, data: Partial<EventAttendee>) =>
    eventsClient.put<EventAttendee>(`/api/v1/attendees/${id}`, data),
  delete: (id: string) => eventsClient.delete(`/api/v1/attendees/${id}`),
  checkIn: (id: string) => eventsClient.post<EventAttendee>(`/api/v1/attendees/${id}/check-in`),
  checkOut: (id: string) => eventsClient.post<EventAttendee>(`/api/v1/attendees/${id}/check-out`),
};

// --- Equipment Service ---
export interface EquipmentListParams {
  page?: number;
  page_size?: number;
  category?: string;
  is_active?: boolean;
}

export interface EquipmentAvailableParams {
  date?: string;
  start_time?: string;
  end_time?: string;
  quantity_needed?: number;
}

export interface AddEquipmentToBookingInput {
  equipment_id: string;
  quantity_requested: number;
  unit_price: number;
  setup_required?: boolean;
  setup_start_time?: string;
  notes?: string;
}

export const equipmentService = {
  list: async (params?: EquipmentListParams): Promise<PaginatedEquipment> => {
    const res = await eventsClient.get<PaginatedEquipment>('/api/v1/event-equipment', { params });
    return res.data;
  },
  getAll: async (): Promise<EventEquipment[]> => {
    const res = await eventsClient.get<EventEquipment[]>('/api/v1/event-equipment/all');
    return res.data;
  },
  getByCategory: async (category: string): Promise<EventEquipment[]> => {
    const res = await eventsClient.get<EventEquipment[]>(
      `/api/v1/event-equipment/category/${category}`,
    );
    return res.data;
  },
  getAvailable: async (params?: EquipmentAvailableParams): Promise<EventEquipment[]> => {
    const res = await eventsClient.get<EventEquipment[]>('/api/v1/event-equipment/available', {
      params,
    });
    return res.data;
  },
  get: (id: string) => eventsClient.get<EventEquipment>(`/api/v1/event-equipment/${id}`),
  create: (data: Partial<EventEquipment>) =>
    eventsClient.post<EventEquipment>('/api/v1/event-equipment', data),
  update: (id: string, data: Partial<EventEquipment>) =>
    eventsClient.put<EventEquipment>(`/api/v1/event-equipment/${id}`, data),
  delete: (id: string) => eventsClient.delete(`/api/v1/event-equipment/${id}`),
  getBookingEquipment: (bookingId: string) =>
    eventsClient.get<EventBookingEquipment[]>(`/api/v1/event-bookings/${bookingId}/equipment`),
  addToBooking: (bookingId: string, data: AddEquipmentToBookingInput) =>
    eventsClient.post<EventBookingEquipment>(`/api/v1/event-bookings/${bookingId}/equipment`, data),
  removeFromBooking: (equipmentId: string) =>
    eventsClient.delete(`/api/v1/event-equipment/booking/${equipmentId}`),
  markReturned: (equipmentId: string, condition?: string, damageNotes?: string) =>
    eventsClient.put<EventBookingEquipment>(
      `/api/v1/event-equipment/booking/${equipmentId}/return`,
      {
        condition,
        damage_notes: damageNotes,
      },
    ),
};

// ============ GROUP EVENTS TYPES ============

export type GroupStatus = 'active' | 'inactive' | 'archived';
export type GroupType = 'family' | 'corporate' | 'wedding' | 'social' | 'other';
export type GroupEventStatus =
  | 'draft'
  | 'inquiry'
  | 'quoted'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'cancelled';
export type GroupEventPaymentStatus = 'pending' | 'deposit_paid' | 'partial' | 'fully_paid';
export type MemberRole = 'general' | 'vip' | 'speaker' | 'host' | 'mc' | 'organizer';
export type AllocationStatus = 'pending' | 'checked_in' | 'checked_out' | 'cancelled';

export interface Group {
  id: string;
  group_name: string;
  group_type?: GroupType;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string;
  is_recurring?: boolean;
  status: GroupStatus;
  created_at: string;
  updated_at?: string;
  members?: GroupMember[];
  events?: GroupEvent[];
}

export interface GroupMember {
  id: string;
  group_id: string;
  first_name: string;
  last_name: string;
  id_number?: string;
  id_type?: 'national_id' | 'passport' | 'other';
  email?: string;
  phone?: string;
  dietary_restrictions?: string;
  qr_code?: string;
  qr_token_expires_at?: string;
  event_id?: string;
  role: MemberRole;
  status: string;
  checked_in: boolean;
  check_in_time?: string;
  check_in_staff?: string;
  check_out_time?: string;
  special_notes?: string;
  created_at: string;
  updated_at?: string;
  allocations?: MemberAllocation[];
}

export interface BudgetItem {
  id?: string;
  name: string;
  category: 'catering' | 'beverages' | 'venue' | 'equipment' | 'custom';
  quantity: number;
  unit_price: number;
  total_price?: number;
  is_predefined: boolean;
  predefined_item_id?: string;
  product_id?: string;
  entitlement_type?: 'per_guest' | 'per_event';
  max_per_guest?: number;
  served_count?: number;
}

export interface VerificationItem {
  id: string;
  name: string;
  icon?: string;
  required: boolean;
}

export interface GroupEvent {
  id: string;
  group_id: string;
  group_name: string;
  event_date: string;
  end_date?: string;
  start_time: string;
  end_time: string;
  venue: string;
  event_space_id?: string;
  event_space?: EventSpace;
  selected_equipment?: EventBookingEquipment[];
  status: GroupEventStatus;
  confirmation_status: 'pending' | 'confirmed' | 'cancelled';
  payment_status: GroupEventPaymentStatus;
  total_members: number;
  checked_in: number;
  checked_out: number;
  budget_items?: BudgetItem[];
  final_budget: number;
  amount_paid: number;
  extras_total: number;
  verification_items?: VerificationItem[];
  event_started_at?: string;
  event_ended_at?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

export interface MemberAllocation {
  id: string;
  group_event_id: string;
  group_member_id: string;
  member?: GroupMember;
  status: AllocationStatus;
  check_in_time?: string;
  check_out_time?: string;
  verified_items?: VerifiedItem[];
  notes?: string;
  created_at: string;
  qr_codes?: GuestQRCode[];
  extra_items?: ExtraItem[];
}

export interface VerifiedItem {
  name: string;
  verified_at: string;
  staff_name: string;
}

export interface GuestQRCode {
  id: string;
  allocation_id: string;
  qr_code: string;
  qr_image_url?: string;
  is_active: boolean;
  expires_at?: string;
  created_at: string;
}

export interface ExtraItem {
  id: string;
  allocation_id: string;
  item_name: string;
  unit_price: number;
  quantity: number;
  total_price: number;
  notes?: string;
  status: string;
  created_at: string;
}

export interface EventLog {
  id: string;
  allocation_id?: string;
  event_id: string;
  member_id?: string;
  member_name?: string;
  action: 'check_in' | 'check_out' | 'verify_item' | 'add_extra' | 'served';
  item_name?: string;
  staff_id?: string;
  staff_name: string;
  details?: string;
  timestamp: string;
}

export interface PaginatedGroups {
  groups: Group[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedGroupEvents {
  events: GroupEvent[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// --- Group Service ---
export const groupService = {
  list: async (params?: { page?: number; page_size?: number }): Promise<PaginatedGroups> => {
    const res = await eventsClient.get<PaginatedGroups>('/api/v1/groups', { params });
    return res.data;
  },
  getAll: async (): Promise<Group[]> => {
    const res = await eventsClient.get<Group[]>('/api/v1/groups/all');
    return res.data;
  },
  get: (id: string) => eventsClient.get<Group>(`/api/v1/groups/${id}`),
  create: (data: Partial<Group>) => eventsClient.post<Group>('/api/v1/groups', data),
  update: (id: string, data: Partial<Group>) =>
    eventsClient.put<Group>(`/api/v1/groups/${id}`, data),
  delete: (id: string) => eventsClient.delete(`/api/v1/groups/${id}`),
  listMembers: async (groupId: string, eventId?: string): Promise<GroupMember[]> => {
    const params: Record<string, string> = {};
    if (eventId) params.event_id = eventId;
    const res = await eventsClient.get<GroupMember[]>(`/api/v1/groups/${groupId}/members`, {
      params,
    });
    return res.data;
  },
  addMember: (groupId: string, data: Partial<GroupMember>) =>
    eventsClient.post<GroupMember>(`/api/v1/groups/${groupId}/members`, data),
  updateMember: (groupId: string, memberId: string, data: Partial<GroupMember>) =>
    eventsClient.put<GroupMember>(`/api/v1/groups/${groupId}/members/${memberId}`, data),
  deleteMember: (groupId: string, memberId: string) =>
    eventsClient.delete(`/api/v1/groups/${groupId}/members/${memberId}`),
  regenerateMemberQR: async (groupId: string, memberId: string): Promise<GroupMember> => {
    const res = await eventsClient.post<GroupMember>(
      `/api/v1/groups/${groupId}/members/${memberId}/regenerate-qr`,
    );
    return res.data;
  },
};

// --- Group Event Service ---
export const groupEventService = {
  list: async (params?: { page?: number; page_size?: number }): Promise<PaginatedGroupEvents> => {
    const res = await eventsClient.get<PaginatedGroupEvents>('/api/v1/group-events', { params });
    return res.data;
  },
  getToday: async (): Promise<GroupEvent[]> => {
    const res = await eventsClient.get<GroupEvent[]>('/api/v1/group-events/today');
    return res.data;
  },
  getActive: async (): Promise<GroupEvent[]> => {
    const res = await eventsClient.get<GroupEvent[]>('/api/v1/group-events/active');
    return res.data;
  },
  get: (id: string) => eventsClient.get<GroupEvent>(`/api/v1/group-events/${id}`),
  create: (data: Partial<GroupEvent>) =>
    eventsClient.post<GroupEvent>('/api/v1/group-events', data),
  update: (id: string, data: Partial<GroupEvent>) =>
    eventsClient.put<GroupEvent>(`/api/v1/group-events/${id}`, data),
  delete: (id: string) => eventsClient.delete(`/api/v1/group-events/${id}`),
  start: (id: string) => eventsClient.post<GroupEvent>(`/api/v1/group-events/${id}/start`),
  end: (id: string) => eventsClient.post<GroupEvent>(`/api/v1/group-events/${id}/end`),
  updateStatus: (id: string, status: GroupEventStatus) =>
    eventsClient.put<GroupEvent>(`/api/v1/group-events/${id}/status`, { status }),
  updatePaymentStatus: (id: string, payment_status: GroupEventPaymentStatus) =>
    eventsClient.put<GroupEvent>(`/api/v1/group-events/${id}/payment-status`, { payment_status }),
  markAsPaid: (id: string, data: { amount: number; method: string; transactionId: string }) =>
    eventsClient.post<GroupEvent>(`/api/v1/group-events/${id}/payment`, {
      amount: data.amount,
      method: data.method,
      transaction_id: data.transactionId,
    }),
  generateQRCodes: (id: string) => eventsClient.post(`/api/v1/group-events/${id}/qr-codes`),
  checkSpaceAvailability: async (params: {
    space_id: string;
    date: string;
    start_time: string;
    end_time: string;
    exclude_id?: string;
  }): Promise<{ available: boolean; conflicts: GroupEvent[] }> => {
    const res = await eventsClient.get<{ available: boolean; conflicts: GroupEvent[] }>(
      '/api/v1/group-events/check-space-availability',
      { params },
    );
    return res.data;
  },
};

// --- QR Service ---
export interface QRScanRequest {
  qr_code: string;
}

export interface QRVerifyRequest {
  allocation_id: string;
  item_name: string;
  staff_id?: string;
  staff_name: string;
}

export interface QRExtraRequest {
  allocation_id: string;
  item_name: string;
  unit_price: number;
  quantity: number;
  notes?: string;
}

export const qrService = {
  scan: async (data: QRScanRequest): Promise<MemberAllocation> => {
    const res = await eventsClient.post<MemberAllocation>('/api/v1/qr/scan', data);
    return res.data;
  },
  verify: async (data: QRVerifyRequest): Promise<{ message: string }> => {
    const res = await eventsClient.post<{ message: string }>('/api/v1/qr/verify', data);
    return res.data;
  },
  addExtra: async (data: QRExtraRequest): Promise<{ message: string }> => {
    const res = await eventsClient.post<{ message: string }>('/api/v1/qr/extra', data);
    return res.data;
  },
  getEventLogs: async (eventId: string): Promise<EventLog[]> => {
    const res = await eventsClient.get<EventLog[]>(`/api/v1/qr/events/${eventId}/logs`);
    return res.data;
  },
  getAllocationLogs: async (allocationId: string): Promise<EventLog[]> => {
    const res = await eventsClient.get<EventLog[]>(`/api/v1/qr/allocations/${allocationId}/logs`);
    return res.data;
  },
};

// --- Export all types as aggregate ---
export const eventsApi = {
  spaceService,
  packageService,
  eventBookingService,
  attendeeService,
  equipmentService,
  groupService,
  groupEventService,
  qrService,
};

export default eventsApi;

// --- Entitlement Types ---
export interface MealEntitlement {
  id: string;
  allocation_id: string;
  budget_item_name: string;
  product_id?: string;
  quantity_total: number;
  quantity_remaining: number;
  unit: string;
  created_at: string;
  updated_at: string;
}

// --- Entitlement Service ---
export const entitlementService = {
  listByAllocation: async (allocationId: string): Promise<MealEntitlement[]> => {
    const res = await eventsClient.get<MealEntitlement[]>(
      `/api/v1/entitlements/allocations/${allocationId}`,
    );
    return res.data;
  },
  redeem: async (
    allocationId: string,
    entitlementId: string,
    staffName: string,
  ): Promise<{ success: boolean; remaining: number; id: string; budget_item_name: string }> => {
    const res = await eventsClient.post<{
      success: boolean;
      remaining: number;
      id: string;
      budget_item_name: string;
    }>(`/api/v1/entitlements/allocations/${allocationId}/redeem/${entitlementId}`, {
      staff_name: staffName,
    });
    return res.data;
  },
  getEventSummary: async (eventId: string): Promise<MealEntitlement[]> => {
    const res = await eventsClient.get<MealEntitlement[]>(
      `/api/v1/entitlements/events/${eventId}/summary`,
    );
    return res.data;
  },
};

export const emailService = {
  sendConfirmation: async (params: {
    template_name: string;
    recipients: string[];
    data: Record<string, unknown>;
  }): Promise<{ message: string }> => {
    const res = await eventsClient.post<{ message: string }>('/api/v1/email/send', params);
    return res.data;
  },
  sendCustom: async (params: {
    subject: string;
    body_html: string;
    recipients: string[];
  }): Promise<{ message: string }> => {
    const res = await eventsClient.post<{ message: string }>('/api/v1/email/send-custom', params);
    return res.data;
  },
};
